import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import moment from "moment";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {useAuth} from "hooks/useAuth";
import {APIGetProfile, APIUpdateProfile} from "features/userProfile/api/profile";
import BoxContent from "shared/ui/BoxContent";
import PageTitle from "shared/ui/PageTitle";
import UserInfo from "app/sidebar/UserInfo";
import FormField from "shared/form/FormField";
import ErrorsList from "shared/ui/ErrorsList";
import UploadButton from "shared/form/UploadButton";
import CustomAvatar from "shared/ui/CustomAvatar";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import DashboardBox from "features/dashboard/components/DashboardBox";
import GenericButton from "shared/ui/GenericButton";
import Loading from "shared/ui/Loading";
import CustomButton from "shared/ui/CustomButton";
import {useServerErrors} from "../../../hooks/useServerErrors";
import NotificationsSwitch from "../components/NotificationsSwitch";

interface IFormProfileEdit {
    lastname: string;
    firstname: string;
    email: string;
    password?: string;
    confirm_password?: string;
    file?: string;
}

const schema = yup
    .object({})
    .required();

const Profile = () => {
    const {user, update} = useAuth();
    const {action} = useParams();
    const navigate = useNavigate();
    const methods = useForm<IFormProfileEdit>({resolver: yupResolver(schema)});
    const {handleServerErrors} = useServerErrors<IFormProfileEdit>(methods.setError);

    const [loading, setLoading] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [checked, setChecked] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<File | null>();
    const [fileUrl, setFileUrl] = useState<string>("");
    const [credits, setCredits] = useState<any[]>([]);

    useEffect(() => {
        setLoading(true);
        APIGetProfile()
            .then((response) => {
                if (response.data.success) {
                    // console.log("getProfile OK", response);
                    setCredits(response.data.data.credits);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                // console.log("getClassroom ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        methods.clearErrors();
        setErrorMessage("");
        setEditMode(action === "editare");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action]);

    const handleUploadedFileChange = (file: File | null, selectedFileUrl: string) => {
        setSelectedFile(file);
        setFileUrl(selectedFileUrl);
    };

    const handleSubmitError = (err: any) => {
        if (err?.response?.status === 400) {
            let errors = err.response.data.data;
            handleServerErrors(errors, {last_name: "lastname", first_name: "firstname", profile_photo: "file"});
        } else {
            update("error", {show: true, code: "A001"});
        }
    };

    const pageActions =
        user.position !== "student" &&
        (editMode ? (
            <GenericButton type="close" onClick={() => navigate(-1)}/>
        ) : (
            <GenericButton type="edit" onClick={() => navigate(`/profil/editare`)}/>
        ));

    const formatExpireAt = (expireAt: string) =>
        moment(expireAt).locale("ro").format("DD.MM.YYYY HH:mm");

    const onSubmit: SubmitHandler<IFormProfileEdit> = (data) => {
        const payload = {...data};
        const hasChanges = Object.keys(payload).some(key => payload[key as keyof typeof payload] !== user[key as keyof typeof user]);

        if (!hasChanges) {
            setErrorMessage("Nu ați modificat niciun câmp.");
            return;
        }

        methods.clearErrors();
        setErrorMessage("");
        update("loading", {show: true, modal: true});

        const formData = new FormData();
        formData.append("last_name", data.lastname);
        formData.append("first_name", data.firstname);
        formData.append("email", data.email);
        formData.append("profile_photo", selectedFile ?? "");
        formData.append("password", data.password ?? "");
        formData.append("confirm_password", data.confirm_password ?? "");
        selectedFile && (data.file = fileUrl);

        APIUpdateProfile(formData)
            .then((response) => {
                if (response.data.success) {
                    if (data.file) {
                        const newData = data;
                        newData.file = response.data.data.value;
                        update("user", {
                            email: newData.email,
                            firstname: newData.firstname,
                            lastname: newData.lastname,
                            profilePhoto: newData.file,
                        });
                    } else {
                        update("user", data);
                    }
                    setEditMode(false);
                    navigate(-1);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(handleSubmitError)
            .finally(() => update("loading", {show: false}));
    };



    return (
        <Box component="main">
            <PageTitle back={editMode} actions={pageActions}>
                Profilul meu
            </PageTitle>
            {loading ? (
                <Loading show={true}/>
            ) : (
                <>
                    {!editMode ? (
                        <>
                            <DashboardBox type="jsx" number={0} title="Datele contului" icon="fal user-pen">
                                <UserInfo small={false}/>
                                <>
                                    {user.tenantNotifications && user.position !== "principal" && (
                                        <NotificationsSwitch />
                                    )}
                                </>
                            </DashboardBox>

                            {credits.length > 0 && (
                                <>
                                    <Typography variant="h3" mt={3} mb={3}>
                                        Credite
                                    </Typography>

                                    <Box mb={3}>
                                        <DashboardBox type="jsx" number={0} title="Credite" icon="fal box-taped">
                                            <>
                                                {credits.map((credit: any, index: any) => (
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        width={{xs: "auto", xl: "50%"}}
                                                        key={index}
                                                    >
                                                        <Box mb={2}>
                                                            <Typography variant="body1">{credit.name}</Typography>

                                                            {credit.expire_at && (
                                                                <Typography component="span" variant="body2"
                                                                            sx={{color: "error.main"}}>
                                                                    (expiră în data
                                                                    de <b>{formatExpireAt(credit.expire_at)}</b>)
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        <Typography variant="h4" mb={2}>{credit.quantity}</Typography>

                                                    </Box>
                                                ))}
                                            </>
                                            <Box mt={2}>
                                                <Typography>* Creditele nu se reportează în luna următoare.</Typography>
                                                <Typography>
                                                    * Testele școlare includ testele de Limba și literatura română,
                                                    Matematică,
                                                    Istorie, Geografie, Fizică, Chimie și Biologie.
                                                </Typography>
                                                <Typography>
                                                    * Testele de BAC/Evaluare Națională se calculeaza in functie de
                                                    numărul de elevi
                                                    din clasele VIII/XII.
                                                </Typography>
                                            </Box>
                                        </DashboardBox>
                                    </Box>
                                </>
                            )}
                        </>
                    ) : (
                        <BoxContent>
                            <Box width="100%">
                                <FormProvider {...methods}>
                                    <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                                        <Grid container spacing={1.5} alignItems="center">
                                            <FormLabelBox title="Nume:"/>

                                            <FormFieldBox>
                                                <FormField
                                                    name="lastname"
                                                    type="text"
                                                    defaultValue={user.lastname}
                                                    label="Nume"
                                                    autoFocus={true}
                                                    errorMessage={methods.formState.errors.lastname?.message}
                                                    error={!!methods.formState.errors.lastname?.message}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="Prenume:"/>
                                            <FormFieldBox>
                                                {!editMode ? (
                                                    user.firstname
                                                ) : (
                                                    <FormField
                                                        name="firstname"
                                                        type="text"
                                                        defaultValue={user.firstname}
                                                        label="Prenume"
                                                        errorMessage={methods.formState.errors.firstname?.message}
                                                        error={!!methods.formState.errors.firstname?.message}
                                                    />
                                                )}
                                            </FormFieldBox>

                                            <FormLabelBox title="E-mail:"/>
                                            <FormFieldBox>
                                                {!editMode ? (
                                                    user.email
                                                ) : (
                                                    <FormField
                                                        name="email"
                                                        type="text"
                                                        defaultValue={user.email}
                                                        label="E-mail"
                                                        errorMessage={methods.formState.errors.email?.message}
                                                        error={!!methods.formState.errors.email?.message}
                                                    />
                                                )}
                                            </FormFieldBox>

                                            <FormLabelBox title="Imagine profil:"/>
                                            <FormFieldBox>
                                                <UploadButton
                                                    name="profilePhoto"
                                                    label="Imagine profil"
                                                    defaultValue=""
                                                    updateFileCb={handleUploadedFileChange}
                                                    errorMessage={methods.formState.errors.file?.message}
                                                    error={!!methods.formState.errors.file?.message}
                                                />
                                            </FormFieldBox>

                                            {fileUrl && selectedFile && (
                                                <>
                                                    <FormLabelBox/>
                                                    <FormFieldBox>
                                                        <CustomAvatar
                                                            small={false}
                                                            firstname={user.firstname}
                                                            lastname={user.lastname}
                                                            profilePhoto={fileUrl}
                                                        />
                                                    </FormFieldBox>
                                                </>
                                            )}

                                            {user.profilePhoto && !selectedFile && (
                                                <>
                                                    <FormLabelBox/>
                                                    <FormFieldBox>
                                                        <CustomAvatar
                                                            small={false}
                                                            firstname={user.firstname}
                                                            lastname={user.lastname}
                                                            profilePhoto={user.profilePhoto}
                                                        />
                                                    </FormFieldBox>
                                                </>
                                            )}

                                            <FormLabelBox title="Parola:"/>
                                            <FormFieldBox>
                                                <FormField
                                                    name="password"
                                                    type="password"
                                                    label="Parolă"
                                                    autoComplete="new-password"
                                                    error={!!methods.formState.errors.password?.message}
                                                    errorMessage={methods.formState.errors.password?.message}
                                                />
                                            </FormFieldBox>

                                            <FormLabelBox title="Confirmă parola:"/>
                                            <FormFieldBox>
                                                <FormField
                                                    name="confirm_password"
                                                    type="password"
                                                    label="Confirmă parola"
                                                    autoComplete="new-password"
                                                    error={!!methods.formState.errors.confirm_password?.message}
                                                    errorMessage={methods.formState.errors.confirm_password?.message}
                                                />
                                            </FormFieldBox>

                                            {editMode && (
                                                <>
                                                    <FormLabelBox/>
                                                    <FormFieldBox>
                                                        <CustomButton submit buttonType="textIcon" bgcolor="dark">
                                                            SALVEAZĂ PROFILUL
                                                        </CustomButton>
                                                        <ErrorsList
                                                            show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                                                    </FormFieldBox>
                                                </>
                                            )}
                                        </Grid>
                                    </form>
                                </FormProvider>
                            </Box>
                        </BoxContent>
                    )}
                </>
            )}
        </Box>
    );
};

export default Profile;
